import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

// Animowana ilustracja „Translating" w stylu line-art pustych stanów (TableEmptyState):
// karta dokumentu z cyklem glifów językowych (A → あ → 文 → Я), obracającym się
// globusem (akcent teal) i przesuwającym się „shimmerem" wiersza. Animacje gasną
// przy `prefers-reduced-motion`.

const ACCENT = '#0a7465';

const floaty = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Każdy glif widoczny w swoim oknie ~1/4 cyklu (cross-fade kolejnych języków).
const glyphCycle = keyframes`
  0%, 4% { opacity: 0; transform: translateY(5px); }
  9%, 21% { opacity: 1; transform: translateY(0); }
  26%, 100% { opacity: 0; transform: translateY(-5px); }
`;

const shimmer = keyframes`
  0% { transform: translateX(0); opacity: 0.25; }
  50% { opacity: 0.9; }
  100% { transform: translateX(40px); opacity: 0.25; }
`;

const labelPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};

  svg {
    width: auto;
    height: 96px;
  }

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 3.2s ease-in-out infinite;
  }
  .globe {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${spin} 6s linear infinite;
  }
  .glyph {
    transform-box: fill-box;
    transform-origin: center;
    opacity: 0;
  }
  .g1 { animation: ${glyphCycle} 4.8s ease-in-out infinite; }
  .g2 { animation: ${glyphCycle} 4.8s ease-in-out infinite 1.2s; }
  .g3 { animation: ${glyphCycle} 4.8s ease-in-out infinite 2.4s; }
  .g4 { animation: ${glyphCycle} 4.8s ease-in-out infinite 3.6s; }
  .shimmer { animation: ${shimmer} 1.7s ease-in-out infinite alternate; }

  @media (prefers-reduced-motion: reduce) {
    * { animation: none !important; }
    .glyph.g1 { opacity: 1; }
  }
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 300;
  letter-spacing: 0.2px;
  color: ${ACCENT};
  animation: ${labelPulse} 1.3s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const TranslatingAnimation: FC = () => {
  const { t } = useTranslation();

  return (
    <Wrap>
      <svg viewBox="0 0 160 120" role="img" aria-hidden="true">
        {/* miękki cień */}
        <ellipse cx={74} cy={104} rx={42} ry={5} fill="currentColor" opacity={0.08} />

        <g className="floaty">
          {/* karta dokumentu */}
          <rect {...stroke} x={22} y={26} width={104} height={72} rx={12} fill="currentColor" fillOpacity={0.05} />

          {/* cykl glifów językowych — w jednym miejscu cross-fade */}
          <g>
            <text className="glyph g1" x={74} y={64} textAnchor="middle" fontSize={30} fontWeight={700} fill={ACCENT} stroke="none">A</text>
            <text className="glyph g2" x={74} y={64} textAnchor="middle" fontSize={30} fontWeight={700} fill={ACCENT} stroke="none">あ</text>
            <text className="glyph g3" x={74} y={64} textAnchor="middle" fontSize={30} fontWeight={700} fill={ACCENT} stroke="none">文</text>
            <text className="glyph g4" x={74} y={64} textAnchor="middle" fontSize={30} fontWeight={700} fill={ACCENT} stroke="none">Я</text>
          </g>

          {/* wiersze tekstu + przesuwający się shimmer (tłumaczenie „wypełnia" linię) */}
          <g stroke="currentColor" strokeWidth={4} strokeLinecap="round" opacity={0.28}>
            <path d="M40 80 h68" />
            <path d="M40 90 h44" />
          </g>
          <rect className="shimmer" x={40} y={77} width={26} height={6} rx={3} fill={ACCENT} opacity={0.6} />

          {/* globus (obraca się) — akcent teal, „wiele języków" */}
          <g transform="translate(118 32)">
            <circle r={14} fill={ACCENT} fillOpacity={0.12} stroke={ACCENT} strokeWidth={2} />
            <g className="globe">
              <ellipse rx={5.5} ry={14} fill="none" stroke={ACCENT} strokeWidth={1.6} opacity={0.8} />
              <ellipse rx={12} ry={14} fill="none" stroke={ACCENT} strokeWidth={1.4} opacity={0.5} />
            </g>
            <path d="M-14 0 H14" stroke={ACCENT} strokeWidth={1.6} opacity={0.8} />
          </g>
        </g>
      </svg>

      <Label>{t('events.translating', 'Translating…')}</Label>
    </Wrap>
  );
};
